import {
  Box,
  BoxProps,
  ButtonProps,
  Flex,
  FlexProps,
  Heading,
  HeadingProps,
  LinkProps,
} from "@chakra-ui/react";

import React from "react";
import { OverlayImage } from "../../components";
import { FormFieldProps } from "../../form";

export interface FormWrapperProps {
  formElement: React.ReactNode;
  formTitle: string;
  withImage?: boolean;
  image?: string;
  socialAuthProviders?: SocialAuthProvider[];
  buttonProps?: ButtonProps;
  forgotPasswordProps?: LinkProps;
  formProps?: BoxProps;
  formFieldProps?: FormFieldProps;
  socialButtonProps?: ButtonProps;
  showSocialProviderLabel?: boolean;
  hideRegisterLink?: boolean;
  inlineActions?: boolean;
  actionRowProps?: FlexProps;
  orDividerText?: string;
  orDividerProps?: BoxProps;
  emailFieldProps?: Record<string, unknown>;
  passwordFieldProps?: Record<string, unknown>;
  nameFieldProps?: Record<string, unknown>;
  mobileFieldProps?: Record<string, unknown>;
  registerLinkTextProps?: BoxProps;
  registerLinkProps?: LinkProps;
  passwordNoteProps?: BoxProps;
  loginLinkTextProps?: BoxProps;
  loginLinkProps?: LinkProps;
  hideLoginLink?: boolean;
  confirmPasswordFieldProps?: Record<string, unknown>;
  returnToLoginLinkProps?: LinkProps;
}

export interface FormWrapperExtraProps {
  formWrapperBoxProps?: FlexProps;
  formContainerProps?: BoxProps;
  formHeaderProps?: HeadingProps;
  formImageProps?: BoxProps;
}
export interface SocialAuthProvider {
  providerName: string;
  providerSvg: string;
}

function FormWrapper({
  formElement,
  formTitle,
  withImage = true,
  image,
  socialAuthProviders,
  forgotPasswordProps,
  formProps,
  buttonProps,
  formWrapperBoxProps,
  formFieldProps,
  formContainerProps,
  formHeaderProps,
  formImageProps,
  socialButtonProps,
  showSocialProviderLabel,
  hideRegisterLink,
  inlineActions,
  actionRowProps,
  orDividerText,
  orDividerProps,
  emailFieldProps,
  passwordFieldProps,
  nameFieldProps,
  mobileFieldProps,
  registerLinkTextProps,
  registerLinkProps,
  passwordNoteProps,
  loginLinkTextProps,
  loginLinkProps,
  hideLoginLink,
  confirmPasswordFieldProps,
  returnToLoginLinkProps,
}: FormWrapperProps & FormWrapperExtraProps) {
  const showImage = withImage && image;
  const elementToRender = React.isValidElement<{
    socialAuthProviders?: SocialAuthProvider[];
    buttonProps?: ButtonProps;
    formProps?: BoxProps;
    forgotPasswordProps?: LinkProps;
    formFieldProps?: FormFieldProps;
    socialButtonProps?: ButtonProps;
    showSocialProviderLabel?: boolean;
    hideRegisterLink?: boolean;
    inlineActions?: boolean;
    actionRowProps?: FlexProps;
    orDividerText?: string;
    orDividerProps?: BoxProps;
    emailFieldProps?: Record<string, unknown>;
    passwordFieldProps?: Record<string, unknown>;
    nameFieldProps?: Record<string, unknown>;
    mobileFieldProps?: Record<string, unknown>;
    registerLinkTextProps?: BoxProps;
    registerLinkProps?: LinkProps;
    passwordNoteProps?: BoxProps;
    loginLinkTextProps?: BoxProps;
    loginLinkProps?: LinkProps;
    hideLoginLink?: boolean;
    confirmPasswordFieldProps?: Record<string, unknown>;
    returnToLoginLinkProps?: LinkProps;
  }>(formElement)
    ? React.cloneElement(formElement, {
        socialAuthProviders,
        buttonProps,
        formProps,
        forgotPasswordProps,
        formFieldProps,
        socialButtonProps,
        showSocialProviderLabel,
        hideRegisterLink,
        inlineActions,
        actionRowProps,
        orDividerText,
        orDividerProps,
        emailFieldProps,
        passwordFieldProps,
        nameFieldProps,
        mobileFieldProps,
        registerLinkTextProps,
        registerLinkProps,
        passwordNoteProps,
        loginLinkTextProps,
        loginLinkProps,
        hideLoginLink,
        confirmPasswordFieldProps,
        returnToLoginLinkProps,
      })
    : formElement;

  return (
    <Flex
      boxShadow="lg"
      maxW="70rem"
      mx="auto"
      borderRadius="lg"
      overflow="hidden"
      {...formWrapperBoxProps}
    >
      {showImage && (
        <Box
          flex={1}
          display={{
            base: "none",
            md: "block",
            xl: "block",
          }}
        >
          <OverlayImage
            overlayProps={formImageProps}
            src={image}
            w="full"
            minH="30rem"
            h="full"
            objectPosition="center"
            objectFit="cover"
            alt={formTitle}
          />
        </Box>
      )}
      <Box flex={1} px="3rem" py="1rem" {...formContainerProps}>
        <Heading as="h3" size="xl" {...formHeaderProps}>
          {formTitle}
        </Heading>
        {elementToRender}
      </Box>
    </Flex>
  );
}

export default FormWrapper;
